import React from 'react';
import { ShieldCheckIcon, EyeIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldCheckIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <LockClosedIcon className="h-6 w-6 text-green-600 mr-2" />
              1. Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              At Rightoz, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, store, and protect your information when you use our 
              milk delivery services, website, and mobile application.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-3">📋 Personal Information</h3>
                <ul className="list-disc list-inside space-y-2 text-blue-700 text-sm">
                  <li>Name and contact details (phone number, email address)</li>
                  <li>Delivery address and location information</li>
                  <li>Payment information (payment method preferences)</li>
                  <li>Order history and subscription preferences</li>
                  <li>Communication preferences and feedback</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="font-semibold text-green-800 mb-3">📱 Technical Information</h3>
                <ul className="list-disc list-inside space-y-2 text-green-700 text-sm">
                  <li>Device information (mobile device type, operating system)</li>
                  <li>App usage data and feature interactions</li>
                  <li>Location data (for delivery purposes)</li>
                  <li>Log files and crash reports</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-3">🚚 Service Information</h3>
                <ul className="list-disc list-inside space-y-2 text-yellow-700 text-sm">
                  <li>Delivery schedules and preferences</li>
                  <li>Product preferences and consumption patterns</li>
                  <li>Customer service interactions</li>
                  <li>Quality feedback and ratings</li>
                  <li>Subscription and billing information</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>We use your personal information for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Service Delivery:</strong> Process and fulfill your milk delivery orders</li>
                <li><strong>Communication:</strong> Send order confirmations, delivery updates, and service notifications</li>
                <li><strong>Payment Processing:</strong> Handle billing and payment transactions securely</li>
                <li><strong>Customer Support:</strong> Respond to inquiries and resolve service issues</li>
                <li><strong>Service Improvement:</strong> Analyze usage patterns to enhance our services</li>
                <li><strong>Marketing:</strong> Send promotional offers and updates (with your consent)</li>
                <li><strong>Compliance:</strong> Meet legal and regulatory requirements</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <EyeIcon className="h-6 w-6 text-green-600 mr-2" />
              4. Information Sharing and Disclosure
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-3">✅ When We May Share:</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                    <li>With delivery partners for order fulfillment</li>
                    <li>With payment processors for transaction handling</li>
                    <li>With service providers who assist our operations</li>
                    <li>When required by law or legal process</li>
                    <li>To protect our rights and prevent fraud</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                  <h3 className="font-semibold text-red-800 mb-3">❌ We Never Share For:</h3>
                  <ul className="list-disc list-inside space-y-2 text-red-700 text-sm">
                    <li>Commercial marketing by third parties</li>
                    <li>Data broker or advertising networks</li>
                    <li>Unauthorized promotional purposes</li>
                    <li>Sale or rental to external companies</li>
                    <li>Any purpose without your consent</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security and Protection</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>We implement robust security measures to protect your personal information:</p>
              
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="font-semibold text-green-800 mb-3">🔒 Security Measures:</h3>
                <ul className="list-disc list-inside space-y-2 text-green-700 text-sm">
                  <li><strong>Encryption:</strong> All sensitive data is encrypted in transit and at rest</li>
                  <li><strong>Access Control:</strong> Limited access to personal information on a need-to-know basis</li>
                  <li><strong>Secure Storage:</strong> Data stored on secure servers with regular backups</li>
                  <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                  <li><strong>Staff Training:</strong> Regular privacy and security training for all employees</li>
                  <li><strong>Incident Response:</strong> Immediate response protocols for any security incidents</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>We retain your personal information for as long as necessary to provide our services and comply with legal obligations:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Active Customers:</strong> Data retained while account is active and for reasonable period after</li>
                <li><strong>Transaction Records:</strong> Billing and payment records kept for 7 years as required by law</li>
                <li><strong>Communication Records:</strong> Customer service interactions retained for 2 years</li>
                <li><strong>Marketing Data:</strong> Deleted immediately upon unsubscribe request</li>
                <li><strong>Inactive Accounts:</strong> Data deleted after 3 years of inactivity (with notification)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">You have the following rights regarding your personal information:</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">📖 Access Rights</h4>
                    <p className="text-blue-700 text-sm">Request access to your personal data and information about how it's processed</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">✏️ Correction Rights</h4>
                    <p className="text-green-700 text-sm">Request correction of inaccurate or incomplete personal information</p>
                  </div>
                  
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2">🗑️ Deletion Rights</h4>
                    <p className="text-red-700 text-sm">Request deletion of your personal data (subject to legal requirements)</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-2">📤 Portability Rights</h4>
                    <p className="text-yellow-700 text-sm">Request transfer of your data to another service provider</p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-2">🚫 Objection Rights</h4>
                    <p className="text-purple-700 text-sm">Object to processing of your data for marketing purposes</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">⏸️ Restriction Rights</h4>
                    <p className="text-gray-700 text-sm">Request restriction of processing under certain circumstances</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>We use cookies and similar technologies to enhance your experience:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
                <li><strong>Performance Cookies:</strong> Help us understand how you use our services</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with consent)</li>
              </ul>
              <p className="mt-4 text-sm bg-gray-100 p-4 rounded-lg">
                You can control cookie settings through your browser preferences. However, disabling certain cookies may limit website functionality.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <p className="text-orange-800 leading-relaxed">
                Our services are not intended for children under 18 years of age. We do not knowingly collect 
                personal information from children. If we become aware that we have collected personal information 
                from a child without proper consent, we will delete such information immediately.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your personal information is primarily stored and processed in India. If we need to transfer 
              data internationally for service provision, we ensure appropriate safeguards are in place 
              to protect your information in accordance with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
              We will notify you of significant changes via email or app notification. Your continued use of our services 
              after changes become effective constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <p className="text-green-800 mb-4">For privacy-related questions or to exercise your rights, contact us:</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Privacy Officer:</strong></p>
                  <p>📧 privacy@rightoz.com</p>
                  <p>📞 +91 91144 91158</p>
                  <p>💬 WhatsApp: +91 91144 91158</p>
                </div>
                <div>
                  <p><strong>Mailing Address:</strong></p>
                  <p>Rightoz Privacy Department</p>
                  <p>Plot No 48, Deep Vihar Dadi Ka Phatak</p>
                  <p>Benad Road Jhatwara</p>
                  <p>Jaipur, Rajasthan 302012, India</p>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-4">
                We will respond to privacy requests within 30 days of receipt.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Consent</h2>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-blue-800 leading-relaxed">
                By using Rightoz services, you consent to the collection and use of your personal information 
                as described in this Privacy Policy. You can withdraw your consent at any time by contacting 
                us, though this may limit our ability to provide certain services.
              </p>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-12">
          <a
            href="/"
            className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
